import { useMemo } from 'react';
import type {
  PermissionKey,
  PermissionConfig,
  UserMembershipTypeModel,
  TeamSummary,
} from '@mesterplan/shared-types';
import { resolvePermissionsForTeam } from '@mesterplan/shared-types';

/**
 * NOTE: this hook is selected only when `idpEnabled === false`
 * (`Boolean(import.meta.env.VITE_IDP_ISSUER_URL)`, a build-time constant), so it is unreachable
 * in production. It is kept compiling rather than deleted because deleting it is a Firebase-exit
 * decision (epic #2597), not a teams decision — §12 O3.
 */

/**
 * Options for the usePermissions hook.
 */
export interface UsePermissionsOptions {
  /** The current user's UID */
  userId?: string;
  /** The user's role in the current company */
  role: UserMembershipTypeModel | null;
  /** The user's team (used for team-based permissions when role is 'user') */
  team?: TeamSummary | null;
  /** The company's permission configuration (from CompanySettingsModel.permissions) */
  permissionConfig?: PermissionConfig | null;
  /**
   * User permission overrides from subcollection (flat format).
   * These take precedence over overrides in permissionConfig.
   */
  userOverrides?: Partial<Record<PermissionKey, boolean>> | null;
}

/**
 * Return type for the usePermissions hook.
 */
export interface UsePermissionsResult {
  /** Check if the user has a specific permission */
  hasPermission: (permission: PermissionKey) => boolean;
  /** All resolved permissions for the user */
  permissions: Record<PermissionKey, boolean>;
  /** Whether the user is an owner */
  isOwner: boolean;
  /** Whether the user is an admin (admin or owner) */
  isAdmin: boolean;
  /** Whether the user is a regular user (not admin or owner) */
  isUser: boolean;
  /** The user's current role */
  role: UserMembershipTypeModel | null;
  /** The user's team */
  team: TeamSummary | null;
}

/**
 * Core permission hook that resolves permissions based on role, company config, and user overrides.
 *
 * This is the shared implementation used by both web and mobile apps.
 * Each platform has a wrapper hook that extracts the necessary data from their respective contexts.
 *
 * Permission resolution order:
 * 1. Start with default permissions for the role
 * 2. Apply company-level role overrides (if configured)
 * 3. Apply user-specific overrides (if configured)
 *
 * Owner role always has all permissions regardless of config.
 *
 * @example
 * ```tsx
 * // In web app
 * const { hasPermission, isAdmin } = usePermissions({
 *   userId: currentUser?.uid,
 *   role: userMembership?.role ?? null,
 *   permissionConfig: selectedCompany?.settings?.permissions,
 * });
 *
 * if (hasPermission('projects.create')) {
 *   // Show create button
 * }
 * ```
 */
export function usePermissions(options: UsePermissionsOptions): UsePermissionsResult {
  const { userId, role, team, permissionConfig, userOverrides } = options;

  // Resolve all permissions based on role, team, and user overrides
  const permissions = useMemo(() => {
    if (!role) {
      // No role means no permissions
      return {} as Record<PermissionKey, boolean>;
    }
    return resolvePermissionsForTeam(role, team, permissionConfig, userId, userOverrides);
  }, [role, team, permissionConfig, userId, userOverrides]);

  // Create the hasPermission function
  const hasPermission = useMemo(() => {
    return (permission: PermissionKey): boolean => {
      if (!role) return false;
      return permissions[permission] ?? false;
    };
  }, [permissions, role]);

  // Role checks
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin' || role === 'owner';
  const isUser = role === 'user';

  return {
    hasPermission,
    permissions,
    isOwner,
    isAdmin,
    isUser,
    role,
    team: team ?? null,
  };
}

/**
 * Helper function to check multiple permissions at once.
 * Returns true if the user has ALL the specified permissions.
 */
export function hasAllPermissions(
  hasPermission: (permission: PermissionKey) => boolean,
  permissions: PermissionKey[]
): boolean {
  return permissions.every((p) => hasPermission(p));
}

/**
 * Helper function to check multiple permissions at once.
 * Returns true if the user has ANY of the specified permissions.
 */
export function hasAnyPermission(
  hasPermission: (permission: PermissionKey) => boolean,
  permissions: PermissionKey[]
): boolean {
  return permissions.some((p) => hasPermission(p));
}
